<template>
  <Form
    class="base-type-edit__form"
    @submit="saveObject"
    v-slot="{ errors, values, setFieldValue, setFieldError }"
    :initial-values="initialValues"
    :validation-schema="saveSchema"
    ref="formOffer"
  >
    <NeedsReviewGuard :in-review="isNeedsReview">
    <FreePackageHint
      v-if="hasFreePackageForOffersBaseType"
      class="mb-4 mb-lg-5"
      :route-query="$route.query"
    />

    <fieldset class="fieldset mb-4 mb-lg-5">
      <legend>Grunddaten</legend>
      <div class="form-floating mb-3">
            <Field name="name"
                   id="form-offer-name"
                   class="form-control"
                   :class="{'is-invalid': errors.name}"
                   autocomplete="off"
                   placeholder="Titel des Angebotes"
            />
            <label class="form-label" for="form-offer-name">Titel des Angebotes</label>
            <ErrorMessage name="name" :class="errorMessageClasses"/>
          </div>

          <PremiumPhoneToggle
            input-id="form-offer-show-tel-fax"
            :locked="hasFreePackageForOffersBaseType"
            :phone-number="organisationPhone"
            :route-query="$route.query"
          />

          <label class="form-label" for="form-offer-nights">Anzahl Übernachtungen</label>
          <Field as="select" name="nights"
                 id="form-offer-nights" class="form-control"
                 :class="{'is-invalid': errors.nights}"
          >
            <option value="">Anzahl auswählen</option>
            <template v-for="nights in Array(32).keys()" :key="nights">
              <option :value="nights">
                <template v-if="nights===0">
                  Ohne Übernachtung
                </template>
                <template v-else>
                  {{ nights }} Nächte / {{ nights + 1 }} Tage
                </template>
              </option>
            </template>
          </Field>
            <ErrorMessage name="nights" :class="errorMessageClasses"/>

          <div class="mt-3">
            <Field type="checkbox" class="form-control"
                   :value="true" :unchecked-value="false" name="bookable_only_with_matching_number_of_nights"
                   v-slot="{ field }">
              <label>
                <input
                  type="checkbox"
                  v-bind="field"
                  :value="true"
                  :disabled="hasFreePackageForOffersBaseType"
                />
                Anfrageformular: Anfrage nur für diese Dauer zulassen
                <span v-if="hasFreePackageForOffersBaseType" class="badge bg-warning text-dark ms-2">premium</span>
              </label>
            </Field>
          </div>
        </fieldset>

        <fieldset class="fieldset fieldset--categories-by-group mb-4 mb-lg-5">
          <legend>Vermarktungskategorien</legend>
          <ErrorMessage name="categories_data" class="mb-3" :class="errorMessageClasses"/>
          <FieldArray name="categories_data" v-slot="{ fields }">
            <div class="base-type-edit__category-groups">
              <div
                v-for="(groupCategories, groupName) in groupedCategoryFields(fields)"
                :key="groupName"
                class="base-type-edit__category-group"
              >
                <h3 class="base-type-edit__category-group-title">{{ groupName }}</h3>
                <div class="category-list">
                  <div
                    v-for="category in groupCategories"
                    :key="category.key"
                    class="category-item"
                  >
                    <Field
                      :name="`categories_data[${category.key}].id`"
                      type="hidden"
                      :value="category.value.id"
                    />

                    <Field
                      type="checkbox"
                      :name="`categories_data[${category.key}].checked`"
                      v-slot="{ field }"
                      :value="true"
                      :unchecked-value="false"
                    >
                      <input
                        type="checkbox"
                        v-bind="field"
                        class="form-check-input"
                        :id="`cb-category-${category.key}`"
                      />
                    </Field>
                    <label :for="`cb-category-${category.key}`">{{ categoryLabel(category.value) }}</label>
                  </div>
                </div>
              </div>
            </div>
          </FieldArray>
          <ErrorMessage name="categories_data" class="mb-3" :class="errorMessageClasses"/>
        </fieldset>

        <HostLocationSelector
          ref="hostLocationSelector"
          base-type="angebote"
          :errors="errors"
          :values="values"
          :initial-values="initialValues"
          :host-objects="hostObjects"
          :set-field-value="setFieldValue"
          :set-field-error="setFieldError"
        />

        <!-- start, end  -->
        <fieldset class="fieldset mb-4 mb-lg-5">
          <legend>Angebotszeitraum</legend>
          <div class="row">
            <div class="col-12 mb-3">
              <Field type="checkbox" :value="true" :unchecked-value="false"  name="skipDates" class="form-control"
                     v-slot="{ field }">
                <label>
                  <input type="checkbox" v-bind="field" :value="true" @change="onSkipDatesChange($event, setFieldValue)"/>
                  Auf Angebotszeitraum verzichten
                </label>
              </Field>
              <div class="alert alert-warning" v-if="values.skipDates">
                Ohne Angebotszeitraum wird das Angebot auf mvp.de nicht mehr dargestellt,
                sobald der User in einem Zeitraum sucht.<br>
                Außerdem können wir Sie nicht automatisch darauf aufmerksam machen,
                dass das Angebot aktualisiert werden muss.
              </div>
            </div>
            <template v-if="!values.skipDates">
              <div class="col-12 col-lg-3">
                <label class="form-label">Startdatum</label>
                <div class="form-group">
                  <Field type="date" name="start_on" class="form-control" :class="{'is-invalid': errors.start_on}">
                  </Field>
                  <ErrorMessage name="start_on" :class="errorMessageClasses"/>
                </div>
              </div>
              <div class="col-12 col-lg-3">
                <label class="form-label">Enddatum</label>
                <div class="form-group">
                  <Field type="date" name="end_on" class="form-control" :class="{'is-invalid': errors.end_on}">
                  </Field>
                  <ErrorMessage name="end_on" :class="errorMessageClasses"/>
                </div>
              </div>
              <Field type="checkbox" class="form-control"
                     :value="true" :unchecked-value="false" name="bookable_only_in_availability" v-slot="{ field }">
                <label>
                  <input
                    type="checkbox"
                    v-bind="field"
                    :value="true"
                    :disabled="hasFreePackageForOffersBaseType"
                  />
                  Anfrageformular: Anfrage nur für Angebotszeitraum zulassen (Kein anderes Datum auswählbar)
                  <span v-if="hasFreePackageForOffersBaseType" class="badge bg-warning text-dark ms-2">premium</span>
                </label>
              </Field>
            </template>
          </div>


        </fieldset>

        <fieldset class="fieldset mb-4 bg-warning p-3" v-if="showImplementTodo"> <!-- Todo: Implement me-->
          <legend>Optional - Sperrzeiten für dieses Angebot
            Legen Sie hier die Zeiträume fest, an denen das Angebot nicht buchbar ist.
          </legend>
          <div class="row">
            <div class="mb-3 col-6">
              <label class="form-label" for="sperrzeitBeginn">Sperrzeit - Beginn (fehlt datepicker oder
                daterange)</label>
              <input class="form-control" id="sperrzeitBeginn" type="text" placeholder="Sperrzeit - Beginn"
                     data-sb-validations="">
            </div>
            <div class="mb-3 col-6">
              <label class="form-label" for="sperrzeitEnde">Sperrzeit - Ende (fehlt datepicker oder
                daterange)</label>
              <input class="form-control" id="sperrzeitEnde" type="text" placeholder="Sperrzeit - Ende"
                     data-sb-validations="required">
              <div class="invalid-feedback" data-sb-feedback="sperrzeitEnde:required">Sperrzeit - Ende muss
                ausgefüllt werden.
              </div>
            </div>
          </div>

          <div>
            <button type="button" class="btn btn-primary">Neue Sperrzeit hinzufügen</button>
          </div>

          <div class="mb-3">
            <label class="form-label d-block">Regelmäßige Sperrzeiten</label>
            <div class="form-check">
              <input class="form-check-input" id="alleWochenendensperren" type="checkbox" name="sperren"
                     data-sb-validations="">
              <label class="form-check-label" for="alleWochenendensperren">alle Wochenenden sperren</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" id="alleFeiertagesperren" type="checkbox" name="sperren"
                     data-sb-validations="">
              <label class="form-check-label" for="alleFeiertagesperren">alle mecklenburgischen Feiertage
                sperren</label>
            </div>
          </div>


          <div class="mb-3">
            <label class="form-label d-block">Wiederholung</label>
            <div class="form-check">
              <input class="form-check-input" id="nachAblaufImmerDenselbenZeitraumWiederholen" type="checkbox"
                     name="wiederholung" data-sb-validations=""/>
              <label class="form-check-label" for="nachAblaufImmerDenselbenZeitraumWiederholen">nach Ablauf immer
                denselben Zeitraum wiederholen</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" id="immerDieFestgelegtenSperrzeitenUbernehmen" type="checkbox"
                     name="wiederholung" data-sb-validations=""/>
              <label class="form-check-label" for="immerDieFestgelegtenSperrzeitenUbernehmen">immer die
                festgelegten Sperrzeiten übernehmen</label>
            </div>
          </div>
        </fieldset>

        <fieldset class="fieldset mb-4 mb-lg-5">
          <legend>Leistungen &amp; Beschreibung</legend>

          <div class="mb-5">
            <label class="form-label" for="form-offer-content">Einleitungstext</label>
            <Field name="introtext" v-slot="{ field, handleChange }">
              <TextEditor :height="180" :is-invalid="!!errors.introtext" v-model="field.value" @editorChange="handleChange"/>
            </Field>
            <ErrorMessage name="introtext" :class="errorMessageClasses"/>
          </div>

          <div class="mb-5">
            <label class="form-label">Leistungen</label>
            <FieldArray name="components_data" v-slot="{ fields, push, remove, move }">
              <Sortable
                  :list="fields"
                  item-key="key"
                  @end="onDragEnd($event, move)"
                  :options="{ handle: '.drag-handle', animation: 150 }"
              >
                <template #item="{ index }">
                  <div class="draggable-item">
                    <ComponentFormFields :index="index"
                                         :errors="errors"
                                         :error-message-classes="errorMessageClasses"
                                         :remove="remove"
                    />
                  </div>
                </template>
              </Sortable>

              <ErrorMessage name="components_data" class="mb-3" :class="errorMessageClasses"/>

              <button type="button" class="btn-primary btn btn-sm" @click="push({ count: null, name: null})">
                <i class="fa-solid fa-plus"></i> Leistung hinzufügen
              </button>
            </FieldArray>
          </div>

          <div class="mb-3">
            <label class="form-label" for="form-offer-content">Beschreibung</label>
            <Field name="content" v-slot="{ field, handleChange }">
              <TextEditor :is-invalid="!!errors.content" :pricing-tables-enabled="true" v-model="field.value" @editorChange="handleChange"/>
            </Field>
            <ErrorMessage name="content" :class="errorMessageClasses"/>
          </div>
        </fieldset>

        <fieldset class="fieldset fieldset--media mb-4 mb-lg-5">
          <legend>Bilder zuweisen</legend>
          <p class="base-type-edit__media-intro">
            <Icon icon="fa-pro-solid:circle-info" />
            Wählen Sie die Bilder für dieses Angebot aus. Das erste Bild wird als Titelbild verwendet.
          </p>
          <Field
            v-slot="{ handleChange }"
            name="asset_ids"
            type="text"
            :value="tmpAssets.map(asset => asset.id)"
          >
            <AssetSelect
              :validation-error="!!errors.asset_ids"
              :lock-type-select="true"
              :base-type="packageBaseType"
              :assets="tmpAssets"
              @assetSelect="onAssetSelectChange($event, handleChange)"
            />
          </Field>
          <ErrorMessage name="asset_ids" class="mb-3" :class="errorMessageClasses"/>
        </fieldset>

        <fieldset class="fieldset mb-4 mb-lg-5">
          <legend>Unterkunftsart des Angebotes</legend>
          <Field
            v-slot="{ handleChange }"
            type="text"
            name="filter_list"
          >
            <MultiSelect
              display="chip"
              :showToggleAll="false"
              @change="handleChange(handleAccommodationTypeChange($event))"
              v-model="getAccommodationsFromFilterList"
              :options="accommodationTypeOptions"
              optionLabel="name"
              placeholder="Unterkunftsart des Angebotes"
            />
            <template v-if="additionalTagOptions.length">
              <label class="form-label mt-4 d-block">Weitere (organisationsspezifische) Tags</label>
              <MultiSelect
                display="chip"
                @change="handleChange(handleAdditionalTagChange($event))"
                v-model="getAdditionalTagsFromFilterList"
                :options="additionalTagOptions"
                optionLabel="name"
                placeholder="weitere Tags"
              />
            </template>
          </Field>
        </fieldset>


        <fieldset class="fieldset mb-4 mb-lg-5">
          <legend>Preise</legend>
          <!-- The website price is shown on the customer's own website only (no fallback
               to price 1); price 1 is the per-person price used for marketing. -->
          <template v-if="hasClientWebsite">
            <div class="row">
              <div class="col-12 col-lg-6 mb-3">
                <div class="form-floating">
                  <Field name="website_price"
                         id="website-price"
                         class="form-control"
                         step="0.01"
                         :value="initialValues.website_price"
                         type="number"
                         :class="{'is-invalid': errors.website_price}"
                         autocomplete="off"
                         placeholder="Preis"
                  />
                  <label class="form-label" for="website-price">Preis auf Ihrer Website in Euro</label>
                  <ErrorMessage name="website_price" :class="errorMessageClasses"/>
                </div>
              </div>
              <div class="col-12 col-lg-6 mb-3">
                <Field
                    v-slot="{ handleChange }"
                    name="website_price_description"
                    type="text"
                    :value="initialValues.website_price_description"
                >
                  <Select @change="handleChange(handlePriceSelectChange($event))"
                          :options="priceSelectOptions"
                          v-model="price2Select"
                          editable
                          optionLabel="name"
                          placeholder="Preisdetails"
                          :class="{'is-invalid': errors.website_price_description}"
                  />
                  <ErrorMessage name="website_price_description" :class="errorMessageClasses"/>
                </Field>
              </div>
            </div>
            <p class="form-text mt-n2 mb-3" data-test="website-price-hint">
              Dieser Preis wird auf {{ websiteHostsLabel }} ausgegeben. Ohne Eingabe zeigen wir dort keinen Preis an.
            </p>
            <p class="mb-2">
              Für die Vermarktung möchten wir Sie bitten, noch einen Preis pro Person zu hinterlegen,
              den wir als Ab-Preis angeben können. Eine Buchbarkeit zu dem Preis muss nicht gegeben sein.
            </p>
          </template>
          <div class="row">
            <div class="col-12 col-lg-6 mb-3">
              <div class="form-floating">
                <Field name="marketing_price"
                       id="price-indication-cent"
                       class="form-control"
                       step="0.01"
                       :value="initialValues.marketing_price"
                       type="number"
                       :class="{'is-invalid': errors.marketing_price}"
                       autocomplete="off"
                       placeholder="Preis"
                />
                <label class="form-label" for="price-indication-cent">{{ hasClientWebsite ? 'Preis pro Person für die Vermarktung in Euro' : '(ab) Preis pro Person in Euro' }}</label>
                <ErrorMessage name="marketing_price" :class="errorMessageClasses"/>
              </div>
            </div>
            <div class="col-12 col-lg-6 mb-3">
              <div class="form-floating">
                <input id="price-indication-description"
                       class="form-control"
                       :value="pricePerPersonLabel"
                       readonly
                       tabindex="-1"
                />
                <label class="form-label" for="price-indication-description">Preisdetails</label>
              </div>
            </div>
          </div>
          <p v-if="legacyPriceDescription" class="form-text text-warning mt-n2" data-test="legacy-price-hint">
            Bisher hinterlegt als „{{ legacyPriceDescription }}“. Bitte prüfen Sie, ob der Preis pro Person gilt.
          </p>
          <p v-if="pricePairWarning(values)" class="form-text text-warning mt-n2" data-test="price-pair-warning">
            {{ pricePairWarning(values) }}
          </p>
        </fieldset>

        <Field v-if="initialValues.id" name="contact_form" v-slot="{ value, handleChange }">
          <ContactFormConfiguration
            :model-value="value"
            :booking-mode="values.inquiry_booking_mode"
            :booking-product-config-id="values.inquiry_booking_product_config_id"
            :external-booking-url="values.external_booking_url"
            :external-booking-system="values.external_booking_system"
            @update:model-value="handleChange"
            @update:booking-mode="setFieldValue('inquiry_booking_mode', $event)"
            @update:booking-product-config-id="setFieldValue('inquiry_booking_product_config_id', $event)"
            @update:external-booking-url="setFieldValue('external_booking_url', $event)"
          />
        </Field>

        <div class="position-relative">
          <div v-if="hasFreePackageForOffersBaseType" class="premium-banderole">
            <span class="premium-banderole__label">premium</span>
          </div>
        <fieldset class="fieldset mb-4 mb-lg-5">
          <legend>Externer Link</legend>
          <p class="text-muted small mb-3">
            Der externe Website-Link steht in Premium-Paketen zur Verfügung.
          </p>
          <div class="row">
            <div class="col-12 col-lg-6 mb-3">
              <div class="form-floating">
                <Field
                  name="external_website_url"
                  id="form-offer-external-website-url"
                  class="form-control"
                  :class="{'is-invalid': errors.external_website_url}"
                  type="text"
                  placeholder="https://..."
                />
                <label class="form-label" for="form-offer-external-website-url">Externe Website URL</label>
                <ErrorMessage name="external_website_url" :class="errorMessageClasses"/>
              </div>
            </div>
          </div>
          <PremiumFeatureHint
            :visible="hasFreePackageForOffersBaseType"
            :route-query="$route.query"
          />
        </fieldset>
        </div>

    <BaseTypeFormToolbar
      :errors="errors"
      :object-name="values.name"
      :package-base-type="packageBaseType"
      v-model:marketing-prompt="marketingPrompt"
      :enabled="publicationState.enabled"
      :marketed="publicationState.setting_enable_in_manet_stadt_abl"
      :has-website="showPortalToggle"
      :busy="publicationBusy"
      :activation-disabled="!publicationContextReady"
      :publication-errors="publicationErrors"
      :publication-message="publicationMessage"
      @publication-action="runPublicationAction($event, values)"
      :admin2-object-id="initialValues?.id"
      :admin2-lang="initialValues?.lang || 'de'"
      :preview-url="previewUrl"
      :show-premium-preview="hasFreePackageForOffersBaseType"
    >
      <template #controls>
        <span v-if="!publicationContextReady" class="form-text">
          {{ websiteStore.loading ? 'Website-Informationen werden geladen …' : 'Website-Informationen fehlen. Bitte laden Sie die Seite erneut.' }}
        </span>
        <ObjectNeedsReviewIcon
          v-if="isNeedsReview"
          class="base-type-edit__toolbar-review"
          :label="reviewLabel"
        />
      </template>
    </BaseTypeFormToolbar>
    </NeedsReviewGuard>
  </Form>
</template>


<script>
import {useObjectStore} from "@/stores/object";
import {useUserStore} from "@/stores/user";
import {Form, Field, ErrorMessage, FieldArray} from "vee-validate";
import Yup from '@/yup'
import TextEditor from "@/components/TextEditor.vue";
import FreePackageHint from "@/components/base_type/FreePackageHint.vue";
import PremiumPhoneToggle from "@/components/base_type/PremiumPhoneToggle.vue";
import PremiumFeatureHint from "@/components/base_type/PremiumFeatureHint.vue";
import {
  hasFreePackageForBaseType,
  MAX_OBJECT_IMAGES,
  MAX_OBJECT_NAME_LENGTH,
} from '@/utils/objectFormPolicy';
import ComponentFormFields from "@/pages/offers/ComponentFormFields.vue";
import {Sortable} from 'sortablejs-vue3';
import AssetSelect from "@/components/general/AssetSelect.vue";
import {useMediaStore} from "@/stores/media";
import {useWebsitesStore} from "@/stores/websites";
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import objectPublication from '@/mixins/objectPublication';
import BaseTypeFormToolbar from "@/components/base_type/BaseTypeFormToolbar.vue";
import NeedsReviewGuard from "@/components/base_type/NeedsReviewGuard.vue";
import HostLocationSelector from "@/components/objects/HostLocationSelector.vue";
import ContactFormConfiguration from "@/components/objects/ContactFormConfiguration.vue";
import ObjectNeedsReviewIcon from "@/components/objects/ObjectNeedsReviewIcon.vue";
import {OBJECT_REVIEW_LABEL} from "@/utils/objectReview";
import {PRICE_PER_PERSON, isPerPersonDescription, pricePairWarning, websiteHosts} from "@/utils/offerPrices";
import {buildObjectPreviewUrl} from "@/utils/objectPreview";
import axios from "axios";
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  shouldSearchPhoton,
} from "@/scripts/locationSearch";

export default {
  mixins: [objectPublication],
  components: {
    AssetSelect,
    BaseTypeFormToolbar,
    ObjectNeedsReviewIcon,
    ErrorMessage,
    Form,
    Field,
    FieldArray,
    TextEditor,
    FreePackageHint,
    PremiumPhoneToggle,
    PremiumFeatureHint,
    ComponentFormFields,
    Sortable,
    Select,
    MultiSelect,
    NeedsReviewGuard,
    HostLocationSelector,
    ContactFormConfiguration,
  },
  setup() {
    return {
      objectStore: useObjectStore(),
      userStore: useUserStore(),
      mediaStore: useMediaStore(),
      websiteStore: useWebsitesStore(),
    }
  },
  props: {
    persist: {
      type: Function,
      required: true,
    },
    initialValues: {
      default: () => {
      },
      type: Object
    },
    isNeedsReview: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    hostObjects: {
      type: Array,
      default: () => [],
    },
  },
  data() {

    return {
      schema: Yup.object({
        skipDates: Yup.boolean().optional(),
        name: Yup.string().max(MAX_OBJECT_NAME_LENGTH).required().label('Titel des Angebotes'),
        start_on: Yup.date().when(
            'skipDates',
            {
              is: true,
              then: (schema) => schema.nullable(),
              else: (schema) => schema.required(),
            })
            .transform((curr, orig) => orig === "" ? null : curr)
            .label('Startdatum'),
        end_on: Yup.date().when(
            'skipDates',
            {
              is: true,
              then: (schema) => schema.nullable(),
              else: (schema) => schema.required(),
            }).when(
            'start_on',
            (start_on, schema) => {
              const startDate = Array.isArray(start_on) ? start_on[0] : start_on;
              return startDate ? schema.min(startDate) : schema;
            })
            .transform((curr, orig) => orig === "" ? null : curr)
            .label('Enddatum'),
        nights: Yup.string().required().label('Anzahl der Übernachtungen'),
        configured_host_object_id: Yup.number()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .nullable(),
        location_id: Yup.number()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .nullable()
            .test(
                'host-or-location',
                'Bitte Anbieter / Gastgeber oder Standort auswählen.',
                function (value) {
                  return Boolean(this.parent.configured_host_object_id || value || String(this.parent.search || '').trim())
                }
            ),
        introtext: Yup.string().nullable().max(300).label('Einleitungstext'),
        content: Yup.string().required().label('Beschreibung'),
        external_website_url: Yup.string().httpUrl().nullable().label('Externe Website URL'),
        external_booking_url: Yup.string().httpUrl().nullable().label('Link zum Anfrageformular / Buchungssystem')
          .when('inquiry_booking_mode', {
            is: 'external_url',
            then: schema => schema.required(),
          }),
        inquiry_booking_product_config_id: Yup.number().nullable(),
        marketing_price: Yup.string().nullable().label('Preis pro Person für die Vermarktung'),
        marketing_price_description: Yup.string().nullable().label('Preisdetails'),
        website_price: Yup.string().nullable().label('Preis auf Ihrer Website'),
        website_price_description: Yup.string().nullable().label('Preisdetails'),
        asset_ids: Yup.array()
            .min(1, 'Es wird mindestens ein Bild benötigt.')
            .max(MAX_OBJECT_IMAGES, `Es dürfen maximal ${MAX_OBJECT_IMAGES} Bilder ausgewählt werden.`)
            .required('Es wird mindestens ein Bild benötigt.'),
        categories_data: Yup.array()
            .of(
                Yup.object({
                  id: Yup.number().required(),
                  checked: Yup.boolean(),
                })
            )
            .required("Bitte mindestens eine Kategorie auswählen")
            .test(
                "checked-items-validation",
                "Bitte mindestens eine und maximal 3 Kategorien auswählen",
                (arr) => {
                  if (!arr) return false; // array must exist
                  const checkedCount = arr.filter((item) => item.checked).length;
                  return checkedCount >= 1 && checkedCount <= 3;
                }
            ),
        components_data: Yup.array().min(3).of(
            Yup.object({
              count: Yup.number().transform((value, originalValue) =>
                  originalValue === '' ? 0 : value // Convert empty string to null
              ).min(0).nullable().notRequired().label('Anzahl'),
              name: Yup.string().required().label('Beschreibung'),
            })
        ).label('Leistungen'),
      }),
      errorMessageClasses: 'mt-1 d-block invalid-feedback',
      selectedHostObjectId: this.initialValues.configured_host_object_id ?? null,
      offerLocationId: this.initialValues.location_id ?? null,
      offerLocation: this.initialValues.location ? {...this.initialValues.location} : null,
      locationEditorKey: 0,
      selectionEditorOpen: !this.initialValues.configured_host_object_id && !this.initialValues.location_id,
      selectionSnapshot: null,
      addingLocation: false,
      locationSearch: '',
      locationSearchResults: [],
      locationSearchTimer: null,
      priceSelectOptions: [
        {name: 'pro Nacht', value: 'pro Nacht'},
        {name: 'Gesamtpreis', value: 'Gesamtpreis'},
        {name: 'pro Teilnehmer', value: 'pro Teilnehmer'},
        {name: 'pro Person', value: 'pro Person'},
        {name: 'Gesamtpreis (pro Person)', value: 'Gesamtpreis (pro Person)'},
      ],
      accommodationTypeOptions: [
        {name: 'Hotel', value: 'hotel'},
        {name: 'Pension', value: 'pension'},
        {name: 'Ferienwohnung', value: 'fewo'},
        {name: 'Ferienhaus', value: 'feha'},
        {name: 'Camping', value: 'camping'},
        {name: 'Hostel', value: 'hostel'},
        {name: 'schwimmendes Objekt', value: 'schwimmendeunterkunft'},
        {name: 'Jugendherberge', value: 'jugendherberge'},
      ],
      userSelectedAccommodationTypes: [],
      price2Select: this.initialValues.website_price_description,
      pricePerPersonLabel: PRICE_PER_PERSON,
      showImplementTodo: false,
      filterList: this.initialValues.filter_list || [],
      tmpAssets: [...(this.initialValues.assets || [])],
    }
  },
  created() {
  },
  mounted() {
    this.mediaStore.filters.type = ['images']
    this.userStore.fetchMyLocations().catch((error) => {
      console.error('fetchMyLocations failed', error)
    })
    this.websiteStore.index(this.currentKdnr).catch((error) => {
      console.error('websiteStore.index failed', error)
    })
  },
  beforeUnmount() {
    if (this.locationSearchTimer) {
      clearTimeout(this.locationSearchTimer)
    }
  },
  computed: {
    publicationObject() {
      return this.initialValues
    },
    publicationContextReady() {
      return !!this.currentKdnr && String(this.websiteStore.loadedKdnr || '') === String(this.currentKdnr)
    },
    publicationForm() {
      return this.$refs.formOffer
    },
    reviewLabel() {
      return OBJECT_REVIEW_LABEL
    },
    currentKdnr() {
      return this.userStore.user?.organisation?.kdnr || this.$route.query.kdnr || null
    },
    hasClientWebsite() {
      return String(this.websiteStore.loadedKdnr || '') === String(this.currentKdnr || '')
        && this.websiteStore.items.length > 0
    },
    showPortalToggle() {
      return this.hasClientWebsite
    },
    websiteHostsLabel() {
      return websiteHosts(this.websiteStore.items).join(', ') || 'Ihrer Website'
    },
    // Price 1 used to have a free description; show a former non-per-person one
    // once, because saving stores price 1 as "pro Person".
    legacyPriceDescription() {
      const description = this.initialValues.marketing_price_description
      if (!description || !this.initialValues.marketing_price) return null
      return isPerPersonDescription(description) ? null : description
    },
    filterListItems() {
      return Array.isArray(this.filterList) ? this.filterList : []
    },
    additionalTagOptions() {
      const tags = this.userStore.user.organisation?.client_tags
      return Array.isArray(tags) ? tags : []
    },
    getAccommodationsFromFilterList() {
      const filteredOptions = this.accommodationTypeOptions.filter(option => this.filterListItems.includes(option.value));
      return filteredOptions
    },
    getAdditionalTagsFromFilterList() {
      const filteredOptions = this.additionalTagOptions.filter(option => this.filterListItems.includes(option.value));
      return filteredOptions
    },
    packageBaseType() {
      return 'angebote'
    },
    hasFreePackageForOffersBaseType() {
      return hasFreePackageForBaseType(
        this.userStore.user?.organisation?.active_packages,
        this.packageBaseType,
      )
    },
    organisationPhone() {
      return this.userStore.user?.organisation?.phone || ''
    },
    hasManyLocations() {
      return this.userStore.myLocationsTotal > 7 || this.userStore.myLocations.length > 7
    },
    displayedLocations() {
      let locations
      if (this.locationSearch.trim()) {
        locations = this.locationSearchResults
      } else if (this.hasManyLocations) {
        locations = this.userStore.myLocations.slice(0, 7)
      } else {
        locations = this.userStore.myLocations
      }

      const selectedLocation = this.userStore.myLocations.find(
        (location) => Number(location.id) === Number(this.offerLocationId)
      )
      if (selectedLocation && !locations.some((location) => Number(location.id) === Number(selectedLocation.id))) {
        return [selectedLocation, ...locations]
      }
      return locations
    },
    selectedChoiceType() {
      return this.selectedHostObjectId ? 'Ausgewählter Anbieter / Gastgeber' : 'Ausgewählter Ort'
    },
    selectedChoiceLabel() {
      if (this.selectedHostObjectId) {
        return this.hostObjectById(this.selectedHostObjectId)?.name || this.offerLocation?.name || ''
      }
      return this.offerLocation?.name || this.offerLocation?.street || ''
    },
    selectedChoiceAddress() {
      const location = this.selectedHostObjectId
        ? this.hostObjectById(this.selectedHostObjectId)?.location
        : this.offerLocation
      return this.locationAddressLabel(location)
    },
    previewUrl() {
      return buildObjectPreviewUrl(this.initialValues, this.initialValues?.lang || 'de')
    },
  },
  methods: {
    hostObjectTypeLabel(hostObject) {
      return hostObject?.base_type === 'unterkunft' ? 'Unterkunft' : 'Entdecken'
    },
    hostObjectLocationLabel(hostObject) {
      const location = hostObject?.location
      if (!location) {
        return ''
      }
      return [location.name, location.town].filter(Boolean).join(', ')
    },
    hostObjectById(id) {
      return this.hostObjects.find((hostObject) => Number(hostObject.id) === Number(id))
    },
    locationAddressLabel(location) {
      if (!location) {
        return ''
      }
      const zip = location.zip ?? location.postcode
      const town = location.town ?? location.city
      return [location.street, [zip, town].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    },
    openSelectionEditor() {
      this.selectionSnapshot = {
        hostObjectId: this.selectedHostObjectId,
        locationId: this.offerLocationId,
        location: this.offerLocation ? {...this.offerLocation} : null,
      }
      this.selectionEditorOpen = true
      this.addingLocation = false
    },
    handleHostSelection(rawHostObjectId, setFieldValue, setFieldError) {
      const hostObjectId = rawHostObjectId ? Number(rawHostObjectId) : null
      this.selectedHostObjectId = hostObjectId
      setFieldValue('configured_host_object_id', hostObjectId, true)
      const hostObject = this.hostObjectById(hostObjectId)
      this.offerLocationId = hostObject?.location?.id ?? null
      this.offerLocation = hostObject?.location
        ? this.normalizeLocationFromAddress(hostObject.location, this.offerLocationId)
        : null
      setFieldValue('location_id', this.offerLocationId, true)
      setFieldValue('search', '', false)
      setFieldValue('address_name', '', false)
      setFieldError('configured_host_object_id', undefined)
      setFieldError('location_id', undefined)
      setFieldError('search', undefined)
      setFieldError('address_name', undefined)
      this.addingLocation = false
      this.selectionEditorOpen = false
      this.selectionSnapshot = null
    },
    handleLocationSelection(location, setFieldValue, setFieldError) {
      const locationId = Number(location.id)
      this.selectedHostObjectId = null
      this.offerLocationId = locationId
      this.offerLocation = this.normalizeLocationFromAddress(location, locationId)
      setFieldValue('configured_host_object_id', null, true)
      setFieldValue('location_id', locationId, true)
      setFieldValue('search', location.name || this.locationAddressLabel(location), false)
      setFieldValue('address_name', location.name || '', false)
      setFieldError('configured_host_object_id', undefined)
      setFieldError('location_id', undefined)
      setFieldError('search', undefined)
      setFieldError('address_name', undefined)
      this.addingLocation = false
      this.selectionEditorOpen = false
      this.selectionSnapshot = null
    },
    startAddingLocation(setFieldValue, setFieldError) {
      this.addingLocation = true
      this.locationEditorKey += 1
      setFieldError('location_id', undefined)
      setFieldValue('search', '', false)
    },
    handleAddressChange(newAddress, setFieldValue, setFieldError) {
      if (!newAddress?.id && !newAddress?.street) {
        this.offerLocationId = null
        this.offerLocation = null
        setFieldValue('location_id', null, true)
        return
      }

      const locationId = newAddress.id ? Number(newAddress.id) : null
      this.selectedHostObjectId = null
      this.offerLocationId = locationId
      this.offerLocation = this.normalizeLocationFromAddress(newAddress, locationId)
      setFieldValue('configured_host_object_id', null, true)
      setFieldValue('location_id', locationId, true)
      setFieldValue('address_name', newAddress.name || '', false)
      setFieldError('location_id', undefined)
    },
    finishNewLocation(setFieldValue, setFieldError) {
      if (!this.offerLocation?.street || !String(this.offerLocation?.name || '').trim()) {
        return
      }

      this.selectedHostObjectId = null
      setFieldValue('configured_host_object_id', null, true)
      setFieldError('configured_host_object_id', undefined)
      setFieldError('location_id', undefined)
      this.addingLocation = false
      this.selectionEditorOpen = false
      this.selectionSnapshot = null
    },
    cancelSelection(setFieldValue, setFieldError) {
      if (!this.selectionSnapshot) {
        return
      }

      this.selectedHostObjectId = this.selectionSnapshot.hostObjectId
      this.offerLocationId = this.selectionSnapshot.locationId
      this.offerLocation = this.selectionSnapshot.location ? {...this.selectionSnapshot.location} : null
      setFieldValue('configured_host_object_id', this.selectedHostObjectId, true)
      setFieldValue('location_id', this.offerLocationId, true)
      setFieldValue('search', '', false)
      setFieldValue('address_name', this.offerLocation?.name || '', false)
      setFieldError('configured_host_object_id', undefined)
      setFieldError('location_id', undefined)
      this.addingLocation = false
      this.selectionEditorOpen = false
      this.selectionSnapshot = null
      this.locationEditorKey += 1
    },
    scheduleLocationSearch() {
      if (this.locationSearchTimer) {
        clearTimeout(this.locationSearchTimer)
      }

      const query = this.locationSearch.trim()
      if (!query) {
        this.locationSearchResults = []
        return
      }

      this.locationSearchTimer = setTimeout(() => {
        this.userStore.searchMyLocations(query).then((locations) => {
          if (this.locationSearch.trim() === query) {
            this.locationSearchResults = locations
          }
        }).catch((error) => {
          console.error('searchMyLocations failed', error)
        })
      }, 250)
    },
    normalizeLocationFromAddress(address, id = address?.id ?? null) {
      return {
        id,
        name: address.name,
        street: address.street,
        zip: address.zip ?? address.postcode,
        town: address.town ?? address.city,
        country: address.country,
        latitude: address.latitude ?? address.lat,
        longitude: address.longitude ?? address.lon,
      }
    },
    createAddressPayloadFromLocation(location) {
      return {
        name: location.name,
        street: location.street,
        postcode: location.zip,
        city: location.town,
        country: location.country,
        lat: location.latitude,
        lon: location.longitude,
      }
    },
    async resolveLocationFromSearch(values) {
      const query = String(values.search || '').trim()
      if (!shouldSearchPhoton(query)) {
        return null
      }

      const {data} = await axios.get(buildPhotonSearchUrl(query))
      const [feature] = filterPhotonHouseResults(data.features)
      if (!feature) {
        return null
      }

      const properties = feature.properties || {}
      return this.normalizeLocationFromAddress({
        name: values.address_name || properties.name || properties.street,
        street: [properties.street, properties.housenumber].filter(Boolean).join(' '),
        postcode: properties.postcode,
        city: properties.city,
        country: properties.country,
        lat: feature.geometry?.coordinates?.[1],
        lon: feature.geometry?.coordinates?.[0],
      })
    },
    async ensureStandaloneLocationForSubmit(values) {
      const submittedLocationName = String(values.address_name || '').trim()
      const existingLocationName = String(this.offerLocation?.name || '').trim()
      const shouldKeepExistingLocation = values.location_id
        && (!submittedLocationName || submittedLocationName === existingLocationName)

      if (shouldKeepExistingLocation) {
        return Number(values.location_id)
      }

      const location = this.offerLocation
        ? {
          ...this.offerLocation,
          name: submittedLocationName || this.offerLocation.name,
        }
        : await this.resolveLocationFromSearch(values)
      if (!location?.street || !String(location?.name || '').trim()) {
        return null
      }

      const created = await this.userStore.createLocationFromAddress(this.createAddressPayloadFromLocation(location))
      if (!created?.id) {
        return null
      }

      const locationId = Number(created.id)
      this.offerLocationId = locationId
      this.offerLocation = this.normalizeLocationFromAddress(created, locationId)
      values.location_id = locationId
      this.userStore.fetchMyLocations(true)
      return locationId
    },
    onSkipDatesChange(e, setFieldValue) {
      if(!e.target.checked) {
        setFieldValue('start_on', null)
        setFieldValue('end_on', null)
      }
    },
    pricePairWarning(values) {
      if (!this.hasClientWebsite) return null
      return pricePairWarning(values.marketing_price, values.website_price, values.website_price_description)
    },
    handlePriceSelectChange(e) {
      if(typeof e.value.value !== 'undefined') {
        return e.value.value
      }
      return e.value
    },
    handleAdditionalTagChange(e) {
      //strip all client_tags from filter list
      const strippedFilterList = this.filterListItems.filter(item =>
          !this.additionalTagOptions.some(obj => obj.value === item)
      );
      //add the items that are now selected
      const selectedTagValues = Array.isArray(e?.value) ? e.value.map(obj => obj.value) : []
      this.filterList = strippedFilterList.concat(selectedTagValues);
      return this.filterList
    },
    handleAccommodationTypeChange(e) {
      //strip all accommodations from filter list
      const strippedFilterList = this.filterListItems.filter(item =>
          !this.accommodationTypeOptions.some(obj => obj.value === item)
      );
      //add the items that are now selected
      const selectedAccommodationValues = Array.isArray(e?.value) ? e.value.map(obj => obj.value) : []
      this.filterList = strippedFilterList.concat(selectedAccommodationValues);
      return this.filterList
    },
    categoryLabel(category) {
      return category?.name || category?.label || this.categoryName(category?.id) || `#${category?.id}`
    },
    categoryName(id) {
      return this.categories.find((category) => Number(category.id) === Number(id))?.name
    },
    groupedCategoryFields(fields) {
      return fields.reduce((groups, field) => {
        const groupName = field.value.group_name || 'Weitere Kategorien'
        groups[groupName] = groups[groupName] || []
        groups[groupName].push(field)
        return groups
      }, {})
    },
    async persistObject(values) {
      const payload = {...values, asset_ids: this.tmpAssets.map((asset) => asset.id)}
      if (String(payload.marketing_price ?? '').trim() !== '') {
        payload.marketing_price_description = PRICE_PER_PERSON
      }
      delete payload.description
      delete payload.address_client_id
      const updated = await this.persist(payload)
      this.$refs.formOffer.resetForm({values: {...values, ...updated, asset_ids: payload.asset_ids}})
    },
    async persistPublicationState(state) {
      await this.persist({id: this.initialValues.id, lang: this.initialValues.lang || this.$route.params.lang || 'de', ...state})
    },
    onDragEnd(event, move) {
      move(event.oldIndex, event.newIndex);
    },
    onAssetSelectChange(selectedAssets, handleChange) {
      this.tmpAssets = selectedAssets
      handleChange(this.tmpAssets.map(asset => asset.id))
    }
  },
}
</script>

<style scoped lang="scss">
.offer-host-choices {
  display: grid;
  gap: .75rem;
}

.offer-host-choice {
  align-items: flex-start;
  background: #fff;
  border: 1px solid var(--bs-border-color);
  border-radius: .5rem;
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: .75rem;
  padding: 1rem;
  text-align: left;
  width: 100%;

  &:hover,
  &--active {
    background: rgba(var(--bs-primary-rgb), .08);
    border-color: var(--bs-primary);
  }

  small {
    color: var(--bs-secondary);
    display: block;
    margin-top: .125rem;
  }
}

.offer-selection-summary {
  border: 1px solid rgba(31, 122, 85, .24);
  border-left: 4px solid #1f7a55;
  background: #eef8f3;
  padding: 1.15rem 1.25rem;

  &__type {
    color: #176442;
    font-size: .85rem;
    font-weight: 600;
    letter-spacing: .03em;
    margin-bottom: .5rem;
    text-transform: uppercase;
  }
}
</style>
